import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import http from "../../api/apiClient";
import type { Payer, Receipt } from "./contributionsModel";
import { payerLabel } from "./ContributionDetails";

// Remembered e-Boekhouden payers: always to one family, or always ask (benefactor).

/** All remembered payers, collapsed by default. */
export function PayersSection({ payers, familyNames, canManage, onChanged }: {
  payers: Payer[];
  familyNames: Map<string, string>;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm dark:border-white/10 dark:bg-slate-900">
      <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 font-semibold text-slate-600 dark:text-slate-300">
        {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
        {t("finance.payments.payers.title", { count: payers.length, defaultValue: "Remembered payers ({{count}})" })}
      </button>
      {open && (
        <div className="mt-2">
          <p className="mb-2 text-xs text-slate-500">
            {t("finance.payments.payers.help", {
              defaultValue: "Payers from e-Boekhouden Lumen links automatically, or always asks about. Forgetting a payer does not change payments that are already linked.",
            })}
          </p>
          <PayerList payers={payers} familyNames={familyNames} canManage={canManage} onChanged={onChanged} />
        </div>
      )}
    </section>
  );
}

export function PayerList({ title, payers, familyNames, canManage, onChanged, hideWhenEmpty }: {
  title?: string;
  payers: Payer[];
  familyNames: Map<string, string>;
  canManage: boolean;
  onChanged: () => void;
  hideWhenEmpty?: boolean;
}) {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null);
  if (hideWhenEmpty && payers.length === 0) return null;

  const run = async (action: () => Promise<unknown>) => {
    setMessage(null);
    try {
      await action();
      onChanged();
    } catch {
      setMessage(t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." }));
    }
  };

  return (
    <div>
      {title && <h3 className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">{title}</h3>}
      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-white/10 dark:border-white/10">
        {payers.map((payer) => (
          <li key={payer.relationId} data-payer={payer.relationId} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-slate-900 dark:text-white">
                {payerLabel(t, { relationName: payer.relationName, payerIbanMasked: payer.payerIbanMasked } as Receipt)}
              </span>
              <span className="block text-xs text-slate-500">
                {payer.mode === "ASK"
                  ? t("finance.payments.payers.mode_ask", { defaultValue: "Always ask (benefactor)" })
                  : t("finance.payments.payers.mode_family", {
                    name: (payer.familyId && familyNames.get(payer.familyId)) || t("finance.payments.payers.other_family", { defaultValue: "a family outside this school year" }),
                    defaultValue: "Always to family {{name}}",
                  })}
                {payer.linkMethod === "IBAN" ? ` · ${t("finance.payments.linked_by.iban", { defaultValue: "linked by IBAN" })}` : ""}
              </span>
            </span>
            {canManage && payer.mode === "FAMILY" && (
              <button type="button" onClick={() => run(() => http.put(`/finance/payments/payers/${payer.relationId}`, { mode: "ASK" }))}
                className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300">
                {t("finance.payments.payers.make_ask", { defaultValue: "Always ask" })}
              </button>
            )}
            {canManage && (
              <button type="button" onClick={() => run(() => http.delete(`/finance/payments/payers/${payer.relationId}`))}
                aria-label={t("finance.payments.payers.forget_label", { name: payer.relationName || "", defaultValue: "Forget payer {{name}}" })}
                className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:border-white/10">
                {t("finance.payments.payers.forget", { defaultValue: "Forget" })}
              </button>
            )}
          </li>
        ))}
      </ul>
      {message && <p role="alert" className="mt-1 text-xs font-semibold text-rose-600">{message}</p>}
    </div>
  );
}

